'use client';

import { useEffect, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { CampaignOfferProps } from '@core/Entities/OfferPromoContent/OfferPromoContent.entity';
import { isD2cModelVsp } from '@core/Utils/Vehicle/isD2cModelVsp';
import { Promotion } from '@gds/Icons';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { getNiceKenticoContent } from '@kentico/GetNiceKenticoContent';
import { getNiceKenticoContentByType } from '@kentico/GetNiceKenticoContentType';
import { isEmptyRichText } from '@kentico/Utils/IsEmptyRichText';
import { useModal } from '@modal/Hooks/UseModal';

import { campaignOfferStorageAtom, offerModalWithStorageAtom } from './Atoms/OfferModalAtom';
import { OfferModal } from './OfferModal';
import { defaultOfferModalMessages as messages } from './OfferModal.messages';
import { OfferLayoutSkeleton } from './OfferModalSkeleton';

import styles from './OfferModal.module.css';

export const OfferModalLayout = () => {
  const { openModal } = useModal();
  const locale = useAtomValue(localeAtom);
  const flags = useAtomValue(flagsAtom);
  const vehicle = useAtomValue(currentVehicleAtom);

  const canShowFreeDelivery = flags.freeDeliveryOffer;
  const isD2C = isD2cModelVsp(vehicle);
  const isEcom = vehicle?.ecom.isEcom;
  const isD2CorEcom = isD2C || isEcom;
  const [offerModalData, setOfferModalData] = useAtom(offerModalWithStorageAtom);
  const [filteredOffers, setFilteredOffers] = useAtom(campaignOfferStorageAtom);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [dynamicCount, setDynamicCount] = useState<number>(0);

  const fetchOfferData = async () => {
    try {
      const defaultRes = await getNiceKenticoContent<any>('pdp_modal_campaign', 2, locale);

      const campaignRes: CampaignOfferProps[] | null =
        await getNiceKenticoContentByType<CampaignOfferProps>(
          'un_component_campaign_offers',
          2,
          locale,
        );

      // Filter campaign offers based on vehicle eligible products codes
      const eligibleOffers = campaignRes
        ? campaignRes.filter((offer: CampaignOfferProps) =>
            vehicle?.eligibleProducts?.includes(offer.campaignCode),
          )
        : [];

      const count = eligibleOffers.reduce((acc: number, offer: CampaignOfferProps) => {
        let newAcc = acc;
        if (!isEmptyRichText(offer.titleCampaignprimary)) newAcc++;
        if (!isEmptyRichText(offer.titleCampaignsecondary)) newAcc++;
        return newAcc;
      }, 2);

      setDynamicCount(count);
      setOfferModalData(defaultRes || []);
      setFilteredOffers(eligibleOffers);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOfferData();
  }, []);

  const openOfferModal = () => {
    const modalComponent =
      offerModalData && filteredOffers && !isLoading ? (
        <OfferModal offerModalContent={offerModalData} campaignOffers={filteredOffers} />
      ) : null;

    openModal(modalComponent, {
      header: messages?.campaignTitle,
      modalClassName: styles.modalContainer,
      modalBodyClassName: styles.modalBody,
    });
  };

  if (isLoading) {
    return <OfferLayoutSkeleton />;
  }
  if (!isD2CorEcom) return null;
  return (
    <div className={styles.campaignSelectorWrapper} onClick={openOfferModal}>
      <Promotion />

      <div className={styles.layoutBody}>
        {canShowFreeDelivery ? (
          <span>
            <Typography Component="span" variant="body2">
              {dynamicCount} {messages?.campaignDelivery}
            </Typography>{' '}
          </span>
        ) : (
          <span>
            <Typography Component="span" variant="body2">
              {messages?.campaignLayoutOne}
            </Typography>
            <Typography Component="span" variant="body4">
              {messages?.campaignLayoutOneTwo}
            </Typography>{' '}
          </span>
        )}

        <Typography Component="span" variant="body4" className={styles.layoutText}>
          {messages?.findMore}
        </Typography>
        <Typography Component="span" variant="body2" className={styles.layoutTextMobile}>
          {messages?.findMoreMobile}
        </Typography>
      </div>
    </div>
  );
};
